import { createSlice } from '@reduxjs/toolkit';
import {
  fetchPosts,
  fetchPostById,
  createPost,
  updatePost,
  deletePost,
  submitForApproval,
  approvePost,
  rejectPost,
  queuePost,
  cancelPost,
  retryPost,
  fetchStats,
} from '../thunks/socialPostsActions';

const initialState = {
  posts: [],
  currentPost: null,
  stats: null,
  loading: false,
  actionLoading: false,
  error: null,
  filters: {
    status: null,
    platform: null,
    from: null,
    to: null,
    sort: '-scheduled_at',
  },
};

function replacePost(state, post) {
  const idx = state.posts.findIndex((p) => p._id === post._id);
  if (idx !== -1) state.posts[idx] = post;
  if (state.currentPost?._id === post._id) state.currentPost = post;
}

const socialPostsSlice = createSlice({
  name: 'socialPosts',
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters(state) {
      state.filters = initialState.filters;
    },
    setCurrentPost(state, action) {
      state.currentPost = action.payload;
    },
    clearCurrentPost(state) {
      state.currentPost = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch list
      .addCase(fetchPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = action.payload?.posts || action.payload || [];
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to fetch posts';
      })

      // Fetch single
      .addCase(fetchPostById.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(fetchPostById.fulfilled, (state, action) => {
        state.actionLoading = false;
        const post = action.payload?.post || action.payload;
        state.currentPost = post;
        replacePost(state, post);
      })
      .addCase(fetchPostById.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.error || 'Failed to fetch post';
      })

      // Create
      .addCase(createPost.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.actionLoading = false;
        const post = action.payload?.post || action.payload;
        state.posts.unshift(post);
        state.currentPost = post;
      })
      .addCase(createPost.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.error || 'Failed to create post';
      })

      // Update
      .addCase(updatePost.fulfilled, (state, action) => {
        const post = action.payload?.post || action.payload;
        replacePost(state, post);
      })
      .addCase(updatePost.rejected, (state, action) => {
        state.error = action.payload?.error || 'Failed to update post';
      })

      // Delete
      .addCase(deletePost.fulfilled, (state, action) => {
        state.posts = state.posts.filter((p) => p._id !== action.payload);
        if (state.currentPost?._id === action.payload) state.currentPost = null;
      })

      // Stats
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.stats = action.payload?.stats || action.payload;
      });

    // Workflow transitions all follow the same pattern: replace post in list + current
    const workflowThunks = [submitForApproval, approvePost, rejectPost, queuePost, cancelPost, retryPost];
    workflowThunks.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.actionLoading = true;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.actionLoading = false;
          const post = action.payload?.post || action.payload;
          replacePost(state, post);
        })
        .addCase(thunk.rejected, (state, action) => {
          state.actionLoading = false;
          state.error = action.payload?.error || 'Action failed';
        });
    });
  },
});

export const { setFilters, clearFilters, setCurrentPost, clearCurrentPost, clearError } = socialPostsSlice.actions;
export default socialPostsSlice.reducer;
